import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { STATION_TZ, formatDate, formatDateTime, toLocalInput, zoneCity } from '../lib/time'
import {
  type Preset, type WindowValue, PRESET_LABEL, dayRange, isLiveWindow, monthGrid,
  placePopover, windowRange,
} from '../lib/timeWindow'

interface Props {
  value: WindowValue
  onChange: (value: WindowValue) => void
  /** The chart is zoomed inside the window (Reset zoom brings it back). */
  zoomed?: boolean
}

const PRESETS: Preset[] = ['24h', '7d', '30d']
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December']
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** The time window (Explore board): presets that end now, or Custom, a day
 *  calendar whose minute-exact fields sit under "Exact times". Everything
 *  is station time (lib/timeWindow.ts).
 *
 *  "Live" while a preset is chosen and the chart is not zoomed: the window
 *  ends at the moment the preset was chosen, and choosing it again brings
 *  it up to now. */
export function TimeWindow({ value, onChange, zoomed = false,
                             }: Props) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLDivElement>(null)
  const customButton = useRef<HTMLButtonElement>(null)
  const range = windowRange(value)
  // Re-judged every 30 s, so "Live" goes once the window has stood still.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(timer)
  }, [])
  const live = isLiveWindow(value, zoomed, now)
  const demo = import.meta.env.VITE_DEMO_MODE === 'true'

  const close = () => {
    setOpen(false)
    customButton.current?.focus()
  }

  return (
    <div className="time-window">
      <div className="time-window-head">
        <h2>Time window</h2>
        {(demo || live) && (
          <span className={demo ? 'badge' : 'live-badge'}
                title={demo ? 'Synthetic observations from the demonstration archive. This is not a live instrument feed.'
                  : `Ends at ${formatDateTime(range.to)}, when it was chosen. Choose the preset again to bring it up to now; after five minutes this badge goes.`}>
            {demo ? 'Demonstration' : 'Live'}
          </span>
        )}
        {zoomed && <span className="zoom-badge">Zoomed in</span>}
      </div>
      <div ref={anchor} className="segmented" role="group" aria-label="Time window">
        {PRESETS.map((preset) => (
          <button key={preset} type="button"
                  aria-pressed={value.kind === 'preset' && value.preset === preset && !open}
                  onClick={() => { setOpen(false); onChange({ kind: 'preset', preset, at: Date.now() }) }}>
            {PRESET_LABEL[preset]}
          </button>
        ))}
        <button ref={customButton} type="button" aria-haspopup="dialog" aria-expanded={open}
                aria-pressed={open || value.kind === 'custom'}
                onClick={() => setOpen((was) => !was)}>
          Custom
        </button>
      </div>
      <RangeFields value={value} onChange={onChange} />
      {open && (
        <CalendarPopover anchor={anchor} value={value}
                         onCancel={close}
                         onApply={(next) => { onChange(next); close() }} />
      )}
    </div>
  )
}

/** From and Until as fields a reader can type a date and time into (Tamar,
 *  2026-10-29), in station time. What is typed is applied on Enter or on
 *  leaving the field, never on each keystroke: while a year is typed digit
 *  by digit the browser reports it as year 0002, 0020 and so on, and a
 *  chart asked to draw from year 2 fails. Only a range that reads as one,
 *  from 1990 on, is applied; otherwise the chart keeps the window it has
 *  and the field is marked. A preset or the calendar moving the window
 *  moves the fields with it. */
function RangeFields({ value, onChange }: { value: WindowValue; onChange: (value: WindowValue) => void }) {
  const range = windowRange(value)
  const shownFrom = range.valid ? toLocalInput(range.from) : ''
  const shownTo = range.valid ? toLocalInput(range.to) : ''
  const [fromText, setFromText] = useState(shownFrom)
  const [toText, setToText] = useState(shownTo)
  useEffect(() => { setFromText(shownFrom); setToText(shownTo) }, [shownFrom, shownTo])
  const next: WindowValue = { kind: 'custom', fromText, toText }
  const draft = windowRange(next)
  const plausible = draft.valid && draft.from.getUTCFullYear() >= 1990
  const apply = () => {
    if (plausible && (fromText !== shownFrom || toText !== shownTo)) onChange(next)
  }
  const onKey = (event: React.KeyboardEvent) => { if (event.key === 'Enter') apply() }
  return (
    <div className="time-window-range time-window-fields">
      <label><span>From</span>
        <input type="datetime-local" step={60} className="mono" value={fromText} aria-invalid={!plausible}
               onChange={(e) => setFromText(e.target.value)} onBlur={apply} onKeyDown={onKey} /></label>
      <label><span>Until</span>
        <input type="datetime-local" step={60} className="mono" value={toText} aria-invalid={!plausible}
               onChange={(e) => setToText(e.target.value)} onBlur={apply} onKeyDown={onKey} /></label>
    </div>
  )
}

interface PopoverProps {
  anchor: React.RefObject<HTMLElement>
  value: WindowValue
  onCancel: () => void
  onApply: (value: WindowValue) => void
}

/** The day calendar: Monday first, future days disabled, the first click
 *  starts the range and the second ends it, then Apply. Drawn on the page
 *  and fixed beside the window, because the Explore sidebar scrolls and
 *  would clip it; placed in the zoomed page's units (placePopover). */
function CalendarPopover({ anchor, value, onCancel, onApply }: PopoverProps) {
  const id = useId()
  const panel = useRef<HTMLDivElement>(null)
  const now = new Date()
  const today = formatDate(now)
  const current = windowRange(value)
  const [fromText, setFromText] = useState(() => toLocalInput(current.valid ? current.from : now))
  const [toText, setToText] = useState(() => toLocalInput(current.valid ? current.to : now))
  const [pickA, setPickA] = useState<string | null>(null)
  const [pickB, setPickB] = useState<string | null>(null)
  const start = formatDate(current.valid ? current.from : now)
  const [view, setView] = useState(() => ({ y: Number(start.slice(0, 4)), m: Number(start.slice(5, 7)) }))
  const [place, setPlace] = useState<{ left: number; top: number } | null>(null)

  useLayoutEffect(() => {
    const update = () => {
      const a = anchor.current?.getBoundingClientRect()
      const p = panel.current?.getBoundingClientRect()
      if (!a || !p) return
      const zoom = (document.body as HTMLElement & { currentCSSZoom?: number }).currentCSSZoom || 1
      setPlace(placePopover(a, { width: p.width, height: p.height },
                            { width: window.innerWidth, height: window.innerHeight }, zoom))
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [anchor])

  // Held in a ref so a parent re-render (a poll landing) neither re-binds
  // these listeners nor moves focus out of a field being typed in.
  const cancel = useRef(onCancel)
  cancel.current = onCancel
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') cancel.current() }
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node
      if (!panel.current?.contains(target) && !anchor.current?.contains(target)) cancel.current()
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [anchor])

  // Focus moves in once the panel is placed and visible: a visibility-hidden
  // element cannot take focus. The day the window starts on, else the first.
  const placed = place !== null
  useEffect(() => {
    if (!placed) return
    const days = panel.current?.querySelectorAll<HTMLButtonElement>('.cal-day:not(:disabled)')
    const first = [...(days ?? [])].find((d) => d.getAttribute('aria-label') === start) ?? days?.[0]
    first?.focus()
    // Once, on placement; `start` is fixed for the popover's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placed])

  const pick = (day: string) => {
    const [a, b] = pickA && !pickB ? [pickA, day] : [day, null]
    setPickA(a)
    setPickB(b)
    const { from, to } = dayRange(a, b ?? a, STATION_TZ, now)
    setFromText(toLocalInput(from))
    setToText(toLocalInput(to))
  }
  const [lo, hi] = pickA && pickB ? [pickA, pickB].sort() : [pickA, pickA]
  const draft = windowRange({ kind: 'custom', fromText, toText })
  const step = (by: number) => setView(({ y, m }) => {
    const total = y * 12 + (m - 1) + by
    return { y: Math.floor(total / 12), m: (total % 12) + 1 }
  })
  const atToday = view.y * 12 + view.m >= Number(today.slice(0, 4)) * 12 + Number(today.slice(5, 7))

  return createPortal(
    <div ref={panel} className="calendar" role="dialog" aria-labelledby={`${id}-title`}
         style={{ position: 'fixed', left: place?.left ?? 0, top: place?.top ?? 0,
                  visibility: place ? 'visible' : 'hidden' }}>
      <div className="cal-head">
        <button type="button" className="cal-nav" aria-label="Previous month" onClick={() => step(-1)}>‹</button>
        <h3 id={`${id}-title`}>{MONTHS[view.m - 1]} {view.y}</h3>
        <button type="button" className="cal-nav" aria-label="Next month" disabled={atToday}
                onClick={() => step(1)}>›</button>
      </div>
      <table className="cal-grid">
        <thead><tr>{WEEKDAYS.map((d) => <th key={d} scope="col">{d}</th>)}</tr></thead>
        <tbody>
          {monthGrid(view.y, view.m).map((week, i) => (
            <tr key={i}>
              {week.map((day, j) => (
                <td key={j}>
                  {day && (
                    <button type="button" className="cal-day mono"
                            disabled={day > today}
                            aria-pressed={Boolean(lo && hi && day >= lo && day <= hi)}
                            data-edge={day === lo || day === hi ? 'true' : undefined}
                            aria-label={day}
                            onClick={() => pick(day)}>
                      {Number(day.slice(8))}
                    </button>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="cal-hint muted">
        {!pickA ? 'Pick the first day.'
          : !pickB ? 'Pick the last day, or apply for this day only.'
            : `${lo} until ${hi}.`}
      </p>
      <fieldset className="cal-exact">
        <legend>Exact times</legend>
        {/* datetime-local carries no zone of its own: fromLocalInput reads
            it in station time, so the range means what the axis says. */}
        <label className="field"><span>From</span>
          <input type="datetime-local" step={60} value={fromText}
                 onChange={(event) => setFromText(event.target.value)} /></label>
        <label className="field"><span>To</span>
          <input type="datetime-local" step={60} value={toText}
                 onChange={(event) => setToText(event.target.value)} /></label>
        <small className="muted">{zoneCity()} time, {STATION_TZ}</small>
        {!draft.valid && (
          <small className="error">Enter a complete range with the start before the end.</small>
        )}
      </fieldset>
      <div className="cal-actions">
        <button type="button" className="button" onClick={onCancel}>Cancel</button>
        <button type="button" className="button primary" disabled={!draft.valid}
                onClick={() => onApply({ kind: 'custom', fromText, toText })}>
          Apply
        </button>
      </div>
    </div>,
    document.body,
  )
}
